import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';
import Button from '../../../../shared/components/Button';
import * as surveyService from '../../data/services/surveyService';
import './SurveyScreen.css';

const SURVEY_QUESTIONS = [
  {
    id: 'timeSaved',
    label: 'How much time do you think you have saved on average, per week, from using Framework?',
    type: 'text',
    placeholder: 'e.g. 2-3 hours, 30 minutes, etc.',
  },
  {
    id: 'qualityOutput',
    label: 'What has the average quality of output been from Framework prompts? (1–10)',
    type: 'number',
    min: 1,
    max: 10,
    placeholder: '1 to 10',
  },
  {
    id: 'otherBenefits',
    label: 'Are there any other benefits you have found from using Framework prompts?',
    type: 'textarea',
    placeholder: 'Share any other benefits...',
  },
  {
    id: 'suggestions',
    label: 'Any suggestions of what we could do to make Framework better for you or problems to solve for you as it relates to using GenAI?',
    type: 'textarea',
    placeholder: 'Share your suggestions...',
  },
];

const SurveyScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    timeSaved: '',
    qualityOutput: '',
    otherBenefits: '',
    suggestions: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const { showSurvey } = await surveyService.getEligibility();
        setEligible(!!showSurvey);
        if (!showSurvey) {
          navigate('/home', { replace: true });
        }
      } catch (err) {
        console.error('Survey eligibility check failed:', err);
        navigate('/home', { replace: true });
      } finally {
        setCheckingEligibility(false);
      }
    };
    check();
  }, [navigate]);

  const handleChange = (id, value) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await surveyService.submitSurvey({
        timeSaved: formData.timeSaved.trim() || null,
        qualityOutput: formData.qualityOutput ? parseInt(formData.qualityOutput, 10) : null,
        otherBenefits: formData.otherBenefits.trim() || null,
        suggestions: formData.suggestions.trim() || null,
      });
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingEligibility) {
    return (
      <div className="survey-screen">
        <div className="survey-screen-container">
          <div className="survey-screen-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!eligible) return null;

  return (
    <div className="survey-screen">
      <button
        type="button"
        className="survey-screen-back"
        onClick={() => navigate('/home')}
        aria-label="Back to Prompt Library"
      >
        <FaArrowLeft />
        Back
      </button>
      <div className="survey-screen-container">
        <div className="survey-screen-gradient-header" />
        <div className="survey-screen-content">
          <h2 className="survey-screen-title">We'd love your feedback</h2>
          <form onSubmit={handleSubmit} className="survey-screen-form">
            {SURVEY_QUESTIONS.map((q) => (
              <div key={q.id} className="survey-form-input-group">
                <label className="survey-form-label" htmlFor={`survey-${q.id}`}>
                  {q.label}
                </label>
                {q.type === 'textarea' ? (
                  <textarea
                    id={`survey-${q.id}`}
                    className="survey-form-textarea"
                    value={formData[q.id]}
                    onChange={(e) => handleChange(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    rows={4}
                    disabled={submitting}
                  />
                ) : (
                  <input
                    id={`survey-${q.id}`}
                    type={q.type}
                    className="survey-form-input"
                    value={formData[q.id]}
                    onChange={(e) => handleChange(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    min={q.min}
                    max={q.max}
                    disabled={submitting}
                  />
                )}
              </div>
            ))}
            {error && <div className="survey-form-error">{error}</div>}
            <div className="survey-form-actions">
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SurveyScreen;
